import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { queueAuthNotice } from '../AuthToast/AuthToast';
import './account-continue-prompt.scss';

const REMEMBERED_ACCOUNT_NAME_KEY = 'flashroom-rememberedAccountName';
const REMEMBERED_ACCOUNT_EMAIL_KEY = 'flashroom-rememberedAccountEmail';
const ACCOUNT_NAME_KEY = 'flashroom-accountName';
const ACCOUNT_EMAIL_KEY = 'flashroom-accountEmail';
const DISMISSED_KEY = 'flashroom-account-continue-dismissed-at';
const DISMISS_MS = 24 * 60 * 60 * 1000;

function recentlyDismissed() {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || '0');
    return dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_MS;
}

function getRememberedAccount() {
    const email =
        localStorage.getItem(REMEMBERED_ACCOUNT_EMAIL_KEY)?.trim() ||
        localStorage.getItem(ACCOUNT_EMAIL_KEY)?.trim();

    if (!email) return null;

    return {
        email,
        name:
            localStorage.getItem(REMEMBERED_ACCOUNT_NAME_KEY)?.trim() ||
            localStorage.getItem(ACCOUNT_NAME_KEY)?.trim() ||
            email,
    };
}

const AccountContinuePrompt = () => {
    const auth = useAuth();
    const [dismissed, setDismissed] = useState(false);
    const [remembered, setRemembered] = useState<{ name: string; email: string } | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const refreshRemembered = () => {
            setRemembered(getRememberedAccount());
        };

        refreshRemembered();

        window.addEventListener('focus', refreshRemembered);
        window.addEventListener('pageshow', refreshRemembered);
        window.addEventListener('storage', refreshRemembered);

        return () => {
            window.removeEventListener('focus', refreshRemembered);
            window.removeEventListener('pageshow', refreshRemembered);
            window.removeEventListener('storage', refreshRemembered);
        };
    }, [auth.isAuthenticated, auth.isLoading]);

    const canShow = !auth.isLoading && !auth.isAuthenticated && !dismissed && !recentlyDismissed() && !!remembered;

    useEffect(() => {
        setVisible(false);

        if (!canShow) return;

        const timer = window.setTimeout(() => {
            setVisible(true);
        }, 2000);

        return () => window.clearTimeout(timer);
    }, [canShow]);

    if (!canShow || !visible || !remembered) {
        return null;
    }

    const continueLogin = () => {
        queueAuthNotice('login');
        void auth.signinRedirect({
            extraQueryParams: {
                login_hint: remembered.email,
            },
        });
    };

    const dismiss = () => {
        localStorage.setItem(DISMISSED_KEY, Date.now().toString());
        setDismissed(true);
        setVisible(false);
    };

    const initial = remembered.name.trim().charAt(0).toUpperCase() || '?';

    return (
        <div className="account-continue-prompt" role="status" aria-live="polite">
            <button
                type="button"
                className="account-continue-main"
                onClick={continueLogin}
                aria-label={`Continue as ${remembered.email}`}
            >
                <span className="account-continue-avatar" aria-hidden="true">
                    {initial}
                </span>
                <span className="account-continue-copy">
                    <span>Continue as {remembered.name}</span>
                    <span>{remembered.email}</span>
                </span>
            </button>

            <button
                type="button"
                className="account-continue-dismiss"
                onClick={dismiss}
                aria-label="Dismiss account suggestion"
            >
                &times;
            </button>
        </div>
    );
};

export default AccountContinuePrompt;
